import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, ArrowLeft, Loader2, Globe, Timer, Eye } from 'lucide-react';
import axios from '../AxiosInstance.jsx';
import GameCodeInput from './GameCodeInput';

const MultiLobbyHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Create game options
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [turnTimer, setTurnTimer] = useState(null);
  const [visibility, setVisibility] = useState('private');

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/api/multi/create', {
        max_players: maxPlayers,
        turn_timer: turnTimer,
        visibility,
      });
      const gameId = res.data.game_session.id;
      navigate(`/lobby/${gameId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/api/multi/join', { game_code: code });
      const gameId = res.data.game_session.id;
      navigate(`/lobby/${gameId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/30 to-black" />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/accueil')}
            className="p-2 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-700/50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-300" />
          </button>
          <div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Mode Multijoueur
            </h1>
            <p className="text-slate-400 text-sm">Affrontez d'autres joueurs en temps réel</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Join Section */}
        <div className="mb-6 p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-700">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Rejoindre une partie
          </h2>
          <GameCodeInput onJoin={handleJoin} loading={loading} />
        </div>

        {/* Create Section */}
        <div className="p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-700">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-400" />
            Créer une partie
          </h2>

          {!showCreate ? (
            <button
              onClick={() => setShowCreate(true)}
              className="w-full py-4 rounded-xl border border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-blue-400 hover:bg-blue-500/5 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nouvelle partie
            </button>
          ) : (
            <div className="space-y-4">
              {/* Max players */}
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Joueurs max
                </label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button
                      key={n}
                      onClick={() => setMaxPlayers(n)}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                        maxPlayers === n
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Turn timer */}
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Timer className="w-4 h-4" />
                  Timer par tour
                </label>
                <div className="flex gap-2">
                  {[
                    { label: 'Aucun', value: null },
                    { label: '2 min', value: 120 },
                    { label: '5 min', value: 300 },
                    { label: '10 min', value: 600 },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => setTurnTimer(opt.value)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                        turnTimer === opt.value
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Visibilité
                </label>
                <div className="flex gap-2">
                  {[
                    { label: 'Privée', value: 'private', icon: '🔒' },
                    { label: 'Publique', value: 'public', icon: '🌍' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setVisibility(opt.value)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        visibility === opt.value
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      <span>{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Create button */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="flex-2 py-3 px-6 rounded-xl bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-400 hover:to-blue-400 text-white font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Globe className="w-5 h-5" />
                  )}
                  Créer la partie
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiLobbyHub;
